import { useState, useEffect, useRef, useCallback } from 'react';

const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const WARNING_BEFORE_MS = 3 * 60 * 1000;
const THROTTLE_MS = 30 * 1000;

export function useIdleTimer(onLogout) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);

  const handleActivity = useCallback(() => {
    if (warningShownRef.current) return;
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    let throttleTimer = null;
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];

    const throttledHandler = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        handleActivity();
        throttleTimer = null;
      }, THROTTLE_MS);
    };

    events.forEach((event) => window.addEventListener(event, throttledHandler));
    return () => {
      events.forEach((event) => window.removeEventListener(event, throttledHandler));
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [handleActivity]);

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const warningThreshold = IDLE_TIMEOUT_MS - WARNING_BEFORE_MS;

      if (elapsed >= IDLE_TIMEOUT_MS) {
        clearInterval(interval);
        setShowWarning(false);
        warningShownRef.current = false;
        onLogout();
      } else if (elapsed >= warningThreshold && !warningShownRef.current) {
        warningShownRef.current = true;
        setShowWarning(true);
        setCountdown(Math.ceil((IDLE_TIMEOUT_MS - elapsed) / 1000));
      } else if (warningShownRef.current) {
        setCountdown(Math.ceil((IDLE_TIMEOUT_MS - elapsed) / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [onLogout]);

  const stayLoggedIn = useCallback(async () => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
    setShowWarning(false);

    try {
      await fetch(`${process.env.REACT_APP_SERVER_URL}/shofco/api/account/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('sessionToken')}` },
      });
    } catch {
      // Silent — next API call will surface session issues
    }
  }, []);

  return { showWarning, countdown, stayLoggedIn };
}
